import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik, FormikProps } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import { getBenefitEligibilityDate } from 'core'
import { EmploymentStatus } from 'core/application/adminDashboard/managePeople/enums/employmentStatus'
import { ProductIds } from 'core/application/commons/config'
import {
  EmployeeForSaveDto,
  ProductClassForEmployeeForSave,
  filterProductClassesByEmploymentStatus,
  getEmployeeForSave,
} from 'core/application/companies/getEmployeeForSave'
import rehireEmployee from 'core/application/companies/rehireEmployee'
import { NamedItem, NamedItemInt } from 'core/domain/common/namedItem'

import useCurrentProduct from 'common/hooks/useCurrentProduct'

import {
  Alert,
  Button,
  Col,
  DatePicker,
  DefaultSelect,
  Icon,
  Label,
  Modal,
  Option,
  RedAsterisk,
  Row,
  Select,
  Tooltip,
} from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { FormData } from 'common/hireEmployeeUtils'
import { useCommandRequest, useQueryRequest } from 'common/hooks'

type RehireEmployeeModalProps = {
  isOpen: boolean
  onClose: () => void
  onSubmit: () => void
  companyUserId: string
}

const RehireEmployeeModal = ({ isOpen, onClose, onSubmit, companyUserId }: RehireEmployeeModalProps) => {
  const [initialValues, setInitialValues] = useState<FormData | null>(null)
  const [productClasses, setProductClasses] = useState<ProductClassForEmployeeForSave[]>([])
  const [sendSaveCommand, isCommandSaveLoading] = useCommandRequest()
  const [renewalProductClasses, setRenewalProductClasses] = useState<ProductClassForEmployeeForSave[]>([])
  const { t: translate } = useTranslation()
  const product = useCurrentProduct()

  const [employee] = useQueryRequest<EmployeeForSaveDto>(null, () => getEmployeeForSave(companyUserId))
  const [, , sendGetEligibilityDate] = useQueryRequest(null)

  const handleEligibilityDateChange = async (productClassId: string, hireDate: string, formik: any) => {
    if (productClassId) {
      const result = await sendGetEligibilityDate(() => getBenefitEligibilityDate(productClassId, hireDate))
      formik.setFieldValue('benefitEligibilityDate', result)
    } else {
      formik.setFieldValue('benefitEligibilityDate', null)
    }
  }

  const handleSubmit = async (formik: FormikProps<FormData>) => {
    const result = await sendSaveCommand(() =>
      rehireEmployee({
        productClassId: formik.values.productClassId,
        hireDate: formik.values.hireDate,
        companyUserId,
        employmentStatus: formik.values.employmentStatus,
        shouldRecreateCompanyUser: product.productId === ProductIds.Perks,
        renewalProductClassId: formik.values.renewalProductClassId,
      }),
    )
    if (result.completed) {
      onClose()
      onSubmit()
    }
  }

  useEffect(() => {
    if (employee) {
      setInitialValues({
        ...employee,
        hireDate: null,
        productClassId: null,
        renewalProductClassId: null,
        employmentStatus: null,
        benefitEligibilityDate: null,
      })

      setProductClasses(
        employee.employmentStatus
          ? filterProductClassesByEmploymentStatus(
              employee.productClasses,
              employee.employmentStatus,
              employee.currentCompanyTypeId,
            )
          : employee.productClasses,
      )
      setRenewalProductClasses(
        employee.employmentStatus
          ? filterProductClassesByEmploymentStatus(
              employee.renewalProductClasses,
              employee.employmentStatus,
              employee.currentCompanyTypeId,
            )
          : employee.renewalProductClasses,
      )
    }
  }, [employee])

  const footer = (disabled, formik: FormikProps<FormData>) => (
    <div>
      <Button
        onClick={() => {
          onClose()
        }}
      >
        {translate('general.cancel')}
      </Button>
      <Button
        type="primary"
        htmlType="submit"
        className="ml-8"
        disabled={disabled}
        loading={isCommandSaveLoading}
        onClick={() => handleSubmit(formik)}
      >
        {translate('general.submit')}
      </Button>
    </div>
  )

  return (
    initialValues && (
      <Formik enableReinitialize={true} initialValues={initialValues} onSubmit={() => {}}>
        {(formik) => (
          <Modal
            onCancel={onClose}
            visible={isOpen}
            title={translate('admin-dashboard.manage-people.employee.rehire-modal')}
            footer={footer(
              !formValidator(translate, initialValues, productClasses, renewalProductClasses).isValidSync(
                formik.values,
              ),
              formik,
            )}
            width={800}
          >
            <form className="form-manage-people-wrapper pt-0" onSubmit={formik.handleSubmit}>
              <div>
                {initialValues.canEditBenefitData && (
                  <Row>
                    <Col span={24} className="section-block_content-item">
                      <Alert
                        className={'t-alert-info'}
                        type="info"
                        showIcon
                        message={translate('admin-dashboard.manage-people.complete-profile.alert-header')}
                        description={translate('admin-dashboard.manage-people.complete-profile.alert-content')}
                      />
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col span={12} className={'mb-16'}>
                    <div className="mb-8">
                      <RedAsterisk className={'mr-4'} />
                      <Label className="mr-8">
                        {translate('admin-dashboard.manage-people.edit-profile.hire-date-label')}
                      </Label>
                    </div>

                    <DatePicker
                      name="hireDate"
                      placeholder={translate('admin-dashboard.manage-people.edit-profile.hire-date-placeholder')}
                      id="hireDate"
                      noStyles
                      defaultValue={formik.values.hireDate !== null ? moment(formik.values.hireDate) : undefined}
                      hideError={true}
                      disabledDate={(current) => current.isBefore(moment(initialValues.minHireDate))}
                      allowClear={false}
                      onChange={(e) => {
                        formik.values.productClassId &&
                          handleEligibilityDateChange(formik.values.productClassId, e.toString(), formik)
                      }}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col sm={24} md={12} className="section-block_content-item">
                    <DefaultSelect
                      onChange={async (newEmploymentStatus) => {
                        const availableProductClasses = filterProductClassesByEmploymentStatus(
                          initialValues.productClasses,
                          newEmploymentStatus,
                          initialValues.currentCompanyTypeId,
                        )
                        setProductClasses(availableProductClasses)

                        if (availableProductClasses.length == 0) {
                          formik.setFieldValue('productClassId', null)
                          await handleEligibilityDateChange(null, formik.values.hireDate?.toString(), formik)
                        }

                        const availableRenewalClasses = filterProductClassesByEmploymentStatus(
                          initialValues.renewalProductClasses,
                          newEmploymentStatus,
                          initialValues.currentCompanyTypeId,
                        )
                        setRenewalProductClasses(availableRenewalClasses)

                        if (availableRenewalClasses.length == 0) {
                          formik.setFieldValue('renewalProductClassId', null)
                        }

                        if (!initialValues.allowProductClassSelection && availableProductClasses.length > 0) {
                          formik.setFieldValue('productClassId', availableProductClasses[0].id)
                          await handleEligibilityDateChange(
                            availableProductClasses[0].id,
                            formik.values.hireDate?.toString(),
                            formik,
                          )
                        }

                        if (!initialValues.allowProductClassSelection && availableRenewalClasses.length > 0) {
                          formik.setFieldValue('renewalProductClassId', availableRenewalClasses[0].id)
                        }
                      }}
                      showRequiredSign={true}
                      name="employmentStatus"
                      placeholder={'Select employment status'}
                      label={translate('admin-dashboard.manage-people.edit-profile.employment-status-label')}
                    >
                      {formik.values.employmentStatuses.map((e: NamedItemInt) => (
                        <Option key={e.id} value={e.id}>
                          {e.name}
                        </Option>
                      ))}
                    </DefaultSelect>
                  </Col>

                  <Col sm={24} md={12} className="section-block_content-item">
                    {initialValues.allowProductClassSelection &&
                      productClasses.length > 0 &&
                      formik.values.employmentStatus != null && (
                        <DefaultSelect
                          showRequiredSign={true}
                          defaultValue={formik.values.productClassId ? [formik.values.productClassId] : null}
                          name="productClassId"
                          placeholder={'Select benefit class'}
                          label={'Benefit class'}
                          onChange={async (productClassId) => {
                            await handleEligibilityDateChange(
                              productClassId,
                              formik.values.hireDate?.toString(),
                              formik,
                            )
                          }}
                        >
                          {formik.values.productClasses.map((p: NamedItem) => (
                            <Option key={p.id} value={p.id}>
                              {p.name}
                            </Option>
                          ))}
                        </DefaultSelect>
                      )}
                  </Col>
                </Row>

                {formik.values?.benefitEligibilityDate && (
                  <Row>
                    <Col span={24} className="section-block_content-item">
                      <Alert
                        className={'t-alert-info'}
                        type="info"
                        showIcon
                        message="Waiting Period Policy"
                        description="This user will be affected by waiting period policy. Please check the calculated benefit eligibility date below."
                      />
                    </Col>
                  </Row>
                )}

                <Row className="mb-24">
                  <Col span={24}>
                    {formik.values?.benefitEligibilityDate && (
                      <Row>
                        <Col span={24} className="section-block_content-item mb-0">
                          <p className="font-14 mb-0">
                            <span className="mr-8">
                              {translate('admin-dashboard.manage-people.edit-profile.benefit-eligibility-date-label')}
                            </span>
                            <Tooltip
                              title={
                                ProductIds.QsehraId === product.productId
                                  ? translate(
                                      'admin-dashboard.manage-people.edit-profile.benefit-eligibility-date-tooltip',
                                    )
                                  : translate('admin-dashboard.manage-people.complete-profile.tooltip-2')
                              }
                              position="right"
                            >
                              <Icon type="far fa-question-circle" className="info_icon" fontAwesome />
                            </Tooltip>
                          </p>
                          <p className="mb-0">{parseZoneFormat(formik.values.benefitEligibilityDate)}</p>
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>

                <Row>
                  <Col span={24}>
                    <Row>
                      <Col span={15}>
                        {initialValues.allowProductClassSelection &&
                          renewalProductClasses.length > 0 &&
                          formik.values.employmentStatus != null && (
                            <Row>
                              <Col sm={24} md={12} className="section-block_content-item mb-0">
                                <DefaultSelect
                                  name="renewalProductClassId"
                                  placeholder={translate(
                                    'admin-dashboard.manage-people.edit-profile.renewal-employee-class-placeholder',
                                  )}
                                  label={translate(
                                    'admin-dashboard.manage-people.edit-profile.renewal-employee-class-label',
                                  )}
                                >
                                  {formik.values.renewalProductClasses.map((p: NamedItem) => (
                                    <Option key={p.id} value={p.id}>
                                      {p.name}
                                    </Option>
                                  ))}
                                </DefaultSelect>
                              </Col>
                            </Row>
                          )}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
            </form>
          </Modal>
        )}
      </Formik>
    )
  )
}

const formValidator = (translate: any, initialValues: EmployeeForSaveDto, productClasses, renewalProductClasses) =>
  Yup.object({
    employmentStatus: Yup.number().nullable().required(),
    productClassId: Yup.string()
      .nullable()
      .when('employmentStatus', {
        is: (employmentStatus: EmploymentStatus) => productClasses.length > 0,
        then: Yup.string().required(translate('general.required')),
      }),
    renewalProductClassId: Yup.string()
      .nullable()
      .when('employmentStatus', {
        is: (employmentStatus: EmploymentStatus) => renewalProductClasses.length > 1,
        then: Yup.string().required(translate('general.required')),
      }),
    hireDate: Yup.string().required(),
  })

export default RehireEmployeeModal
